import { AmountUtils, cn, PaletteUtils } from '@shared/utils';
import type { CardDetailsResponse, CardsResponse } from '@shared/api';
import { CardItem } from '@widgets';
import { Text } from '@shared/widgets';

interface AccountInfoProps {
  className?: string;
  title?: string;
  type1?: string;
  user?: string;
  amount?: string | number;
  type2?: string;
  icon?: string;
  status?: string;
  children?: React.ReactNode;
  card: CardDetailsResponse;
}

export const AccountInfo = (props: AccountInfoProps) => {
  return (
    <div
      className={cn(
        'bg-white dark:bg-soft-black',
        PaletteUtils.isInsigniaPalette && 'rounded-sm',
        PaletteUtils.isVcfcPalette && 'rounded-lg',
        props.className,
      )}
    >
      <div className='flex justify-between p-7 pb-0'>
        <div className='relative z-10 flex-none flex-grow'>
          <Text className='mb-2 break-words' weight='medium' size='2xl'>
            {props.title}
          </Text>
          {PaletteUtils.isVcfcPalette && <CardItem card={props.card as CardsResponse} />}
          {PaletteUtils.isInsigniaPalette && (
            <>
              <div className={cn(PaletteUtils.isVcfcPalette && 'flex items-center', PaletteUtils.isInsigniaPalette && 'block')}>
                <div className={cn('flex items-center gap-2', PaletteUtils.isVcfcPalette && 'flex-row-reverse')}>
                  <Text weight={PaletteUtils.isVcfcPalette ? undefined : 'medium'} size='xs' color='gray'>
                    {props.type1}
                  </Text>
                  {PaletteUtils.isVcfcPalette && <span className='h-1 w-1 rounded-full bg-ash-blue'></span>}
                  <Text
                    className={cn(PaletteUtils.isVcfcPalette && 'text-dark-wet-asphalt/60')}
                    size={PaletteUtils.isVcfcPalette ? 'sm' : undefined}
                    weight={PaletteUtils.isVcfcPalette ? undefined : 'medium'}
                  >
                    {props.user}
                  </Text>
                </div>
              </div>
              {props.amount != null && (
                <Text className={cn('mt-2', PaletteUtils.isInsigniaPalette && 'font-[Prata]')} size='3xl'>
                  {props.icon}
                  {AmountUtils.transformToAmountWithCurrency(props.amount as number)}
                </Text>
              )}
              {props.type2 && (
                <Text
                  className={cn('mt-2', PaletteUtils.isVcfcPalette && 'mt-1 text-[8px]')}
                  uppercase
                  weight={PaletteUtils.isVcfcPalette ? undefined : 'medium'}
                  size={PaletteUtils.isInsigniaPalette ? 'xs' : undefined}
                  color='gray'
                >
                  {props.type2}
                </Text>
              )}
            </>
          )}
        </div>
        {PaletteUtils.isInsigniaPalette && (
          <div
            className={cn(
              'relative flex h-40 w-48 flex-none flex-col items-center justify-end overflow-hidden rounded-tr-sm pb-4 pl-14 pr-1 pt-7 text-center',
              'before:absolute before:-bottom-[2px] before:right-[calc(100%-2px)] before:z-[1] before:h-[150%] before:w-1/2 before:origin-bottom-right before:rotate-[20deg] before:border before:bg-white dark:before:bg-soft-black',
              PaletteUtils.isInsigniaPalette && 'bg-soft-black before:border-r-primary dark:bg-slate',
              PaletteUtils.isVcfcPalette && 'bg-primary dark:before:border-r-ice',
            )}
          >
            <div
              className={cn(
                'mb-3',
                'before:text-[72px] before:leading-[1] before:content-[attr(data-icon)]',
                'after:absolute after:-bottom-[52px] after:-left-[6px] after:text-[156px] after:leading-[1] after:opacity-5 after:content-[attr(data-icon)]',
                PaletteUtils.isInsigniaPalette && ['before:text-primary', 'after:text-primary'],
                PaletteUtils.isVcfcPalette && ['before:text-white', 'after:text-white'],
              )}
              data-icon={props.icon}
            />
            <Text weight='semibold' color='white'>
              {props.status}
            </Text>
          </div>
        )}
      </div>
      <div className='px-9 pb-6 pt-3'>{props.children}</div>
    </div>
  );
};
